import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGroup } from "@/context/GroupContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Utensils, Fuel, ShoppingBag, Plane, HeartPulse, Receipt, Clapperboard, Circle, ImagePlus, X } from "lucide-react";

const ICONS = { utensils: Utensils, fuel: Fuel, "shopping-bag": ShoppingBag, plane: Plane, "heart-pulse": HeartPulse, receipt: Receipt, clapperboard: Clapperboard, circle: Circle };

export default function AddExpense() {
  const { user } = useAuth();
  const { slug, group, groupId, members: groupMembers } = useGroup();
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [paidBy, setPaidBy] = useState(user.id);
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      if (!groupId) return;
      const cats = await api.get("/categories", { params: { group_id: groupId } });
      setCategories(cats.data);
      setCategoryId(cats.data[0]?.id || "");
      setMembers(groupMembers || []);
      setSelectedMembers((groupMembers || []).map((m) => m.id));
    }
    load();
  }, [groupId, groupMembers]);

  const toggleMember = (id) => {
    setSelectedMembers((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const onReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3_000_000) {
      toast.error("Receipt too large (max 3MB)");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setReceipt(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title required");
    if (!amount || Number(amount) <= 0) return toast.error("Enter valid amount");
    if (!categoryId) return toast.error("Select a category");
    if (selectedMembers.length === 0) return toast.error("Include at least one member");
    setBusy(true);
    try {
      await api.post("/expenses", {
        group_id: groupId,
        title,
        category_id: categoryId,
        amount: Number(amount),
        paid_by: paidBy,
        description,
        receipt,
        expense_date: new Date(expenseDate).toISOString(),
        included_member_ids: selectedMembers,
      });
      toast.success("Expense added");
      navigate(`/${slug}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const share = amount && selectedMembers.length ? (Number(amount) / selectedMembers.length).toFixed(2) : "0.00";

  if (!groupId) return <div className="p-4 text-center text-slate-500">Loading group…</div>;

  return (
    <form onSubmit={submit} className="space-y-5 pb-24 lg:pb-8 lg:max-w-2xl" data-testid="add-expense-form">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">New</div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Add expense</h1>
      </div>

      {/* Amount hero */}
      <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="rounded-3xl p-6 bg-white border border-slate-200 card-soft">
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Amount</Label>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="amount text-4xl font-bold text-slate-400">₹</span>
          <input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="amount text-5xl font-bold text-slate-900 bg-transparent outline-none w-full"
            data-testid="expense-amount-input"
          />
        </div>
        <div className="text-xs text-slate-500 mt-3">
          Each of <b>{selectedMembers.length}</b> members pays <b className="text-emerald-700">₹{share}</b>
        </div>
      </motion.div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Dinner at ABC" className="mt-2 h-12 rounded-2xl" required data-testid="expense-title-input" />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Category</Label>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {categories.map((c) => {
            const Icon = ICONS[c.icon] || Circle;
            const active = c.id === categoryId;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategoryId(c.id)}
                data-testid={`category-${c.name.toLowerCase()}`}
                className={`p-3 rounded-2xl border transition-all text-center active:scale-95 ${
                  active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                }`}
              >
                <Icon className="w-4 h-4 mx-auto" style={active ? {} : { color: c.color }} />
                <div className="text-[11px] font-semibold mt-1">{c.name}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Paid by</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {members.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setPaidBy(m.id)}
              data-testid={`paidby-${m.id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-full border transition-all ${
                paidBy === m.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"
              }`}
            >
              {m.photo && <img src={m.photo} alt="" className="w-6 h-6 rounded-full object-cover" />}
              <span className="text-sm font-semibold">{m.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Date</Label>
        <Input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} className="mt-2 h-12 rounded-2xl" data-testid="expense-date-input" />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Include members</Label>
        <div className="mt-2 space-y-2">
          {members.map((m) => (
            <label
              key={m.id}
              className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-slate-200 cursor-pointer active:scale-[0.99] transition-transform"
              data-testid={`include-${m.id}`}
            >
              <Checkbox
                checked={selectedMembers.includes(m.id)}
                onCheckedChange={() => toggleMember(m.id)}
                className="border-slate-300 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
              />
              {m.photo && <img src={m.photo} alt="" className="w-9 h-9 rounded-full object-cover" />}
              <div className="flex-1">
                <div className="font-semibold text-slate-900 text-sm">{m.name}</div>
                <div className="text-xs text-slate-500">{selectedMembers.includes(m.id) ? `pays ₹${share}` : "excluded"}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Description</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-2 rounded-2xl" placeholder="Optional note" data-testid="expense-description-input" />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Receipt</Label>
        {receipt ? (
          <div className="mt-2 relative">
            <img src={receipt} alt="receipt" className="w-full max-h-64 object-cover rounded-2xl border border-slate-200" />
            <button type="button" onClick={() => setReceipt(null)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label className="mt-2 block cursor-pointer">
            <div className="p-6 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 text-center hover:bg-slate-100 transition-colors">
              <ImagePlus className="w-6 h-6 mx-auto text-slate-400" />
              <div className="text-sm text-slate-500 mt-2">Tap to upload receipt</div>
            </div>
            <input type="file" accept="image/*" className="hidden" onChange={onReceiptChange} data-testid="receipt-input" />
          </label>
        )}
      </div>

      <Button type="submit" disabled={busy} className="w-full h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base" data-testid="expense-save-button">
        {busy ? "Saving…" : "Save expense"}
      </Button>
    </form>
  );
}
